import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, Metric, PageHeader, Pager, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, Pill, SearchBox, Toolbar, useDebounced } from '@/components/inputs';
import DataTable, { Dash, RowLink } from '@/components/admin/DataTable';
import LedgerDialog from '@/components/admin/LedgerDialog';
import { aggOf } from '@/shared/fields';
import {
  LEDGER_FIELDS,
  LEDGER_KINDS,
  LEDGER_KIND_LABELS,
  LEDGER_LABEL_STYLES,
  NEEDS_NUMBERS,
  PERIODS,
  PERIOD_LABELS,
  credits,
  hasNumbers,
  kindLabel,
  num,
  periodFilters,
  profitOf,
  signedMoney,
} from '@/shared/ledger';
import { committed, dayOf, idOf, paged } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const { and, eq, contains } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/ledger', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'BookOpenCheck', label: 'Ledger', section: 'Store', profiles: ['Admin_c', 'Staff_c'], order: 42 };

const PAGE_SIZE = 20;

function combine(list) {
  if (!list.length) return undefined;
  if (list.length === 1) return list[0];
  return and(...list);
}

export default function AdminLedger() {
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('month');
  const [kind, setKind] = useState('all');
  const [needs, setNeeds] = useState(false);
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const q = useDebounced(search.trim());

  useEffect(() => {
    setPage(1);
  }, [q, period, kind, needs]);

  const { data, loading, error, run } = useFetch(() => {
    const f = [...periodFilters(period)];
    if (kind !== 'all') f.push(eq('kind_c', kind));
    if (q) f.push(contains('Name', q));
    if (needs) f.push(NEEDS_NUMBERS());
    let query = sdk.table('ledger_c').select(LEDGER_FIELDS);
    const where = combine(f);
    if (where) query = query.where(where);
    return query.orderByDesc('entry_date_c').page(page, PAGE_SIZE).fetch().then(paged);
  }, [q, period, kind, needs, page]);

  const totals = useFetch(() => {
    const scope = [...periodFilters(period)];
    if (kind !== 'all') scope.push(eq('kind_c', kind));
    const where = combine(scope);
    const needsWhere = combine([...scope, NEEDS_NUMBERS()]);
    const bucket = (id, field, fn, w) => ({ id, fields: [{ field, fn, alias: 'count' }], ...(w ? { where: w } : {}) });
    return sdk
      .table('ledger_c')
      .select([{ field: 'Id' }])
      .aggregate([
        bucket('credits', 'credits_c', 'Sum', where),
        bucket('cost', 'cost_c', 'Sum', where),
        bucket('collected', 'collected_c', 'Sum', where),
        bucket('entries', 'Id', 'Count', where),
        bucket('needs', 'Id', 'Count', needsWhere),
      ])
      .limit(1)
      .fetch()
      .then((res) => {
        if (!res?.success) throw new Error(res?.message || 'Could not load totals');
        return aggOf(res);
      });
  }, [period, kind]);

  const agg = totals.data;
  const cost = agg ? agg('cost') : 0;
  const collected = agg ? agg('collected') : 0;
  const kept = collected - cost;
  const needsCount = agg ? agg('needs') : 0;

  const list = data?.rows ?? [];
  const filtered = !!q || kind !== 'all' || needs;
  const periodLabel = PERIOD_LABELS[period] ?? 'All time';

  function refresh() {
    run();
    totals.run();
  }

  function clear() {
    setSearch('');
    setKind('all');
    setNeeds(false);
  }

  function edit(entry) {
    setEditing(entry);
    setEditOpen(true);
  }

  async function remove(entry) {
    const res = await sdk.table('ledger_c').remove(entry.Id);
    if (!committed(res, 'Entry deleted')) return false;
    refresh();
    return true;
  }

  const columns = [
    {
      label: 'Date',
      className: 'whitespace-nowrap text-muted-foreground',
      render: (e) => (e.entry_date_c ? formatLocalDate(dayOf(e.entry_date_c), DATE_FORMATS.SHORT) : <Dash />),
    },
    {
      label: 'Entry',
      render: (e) => (
        <>
          <button type="button" onClick={() => edit(e)} className="text-left font-medium text-primary hover:underline">
            {e.Name || kindLabel(e.kind_c)}
          </button>
          {e.service_c && <span className="ml-2 text-xs text-muted-foreground">{e.service_c}</span>}
          {idOf(e.order_c) ? (
            <p className="text-xs">
              <RowLink to={`/admin/orders/${idOf(e.order_c)}`} mono>
                {e.order_c?.Name ?? 'View order'}
              </RowLink>
            </p>
          ) : (
            e.notes_c && <p className="max-w-xs truncate text-xs text-muted-foreground">{e.notes_c}</p>
          )}
        </>
      ),
    },
    {
      label: 'Kind',
      className: 'hidden md:table-cell',
      render: (e) => <StatusBadge value={kindLabel(e.kind_c)} styles={LEDGER_LABEL_STYLES} />,
    },
    {
      label: 'Credits',
      className: 'text-right tabular-nums',
      render: (e) => (hasNumbers(e) ? credits(e.credits_c) : <span className="text-xs text-warning">Needs numbers</span>),
    },
    {
      label: 'Cost',
      className: 'hidden sm:table-cell text-right tabular-nums text-muted-foreground',
      render: (e) => (num(e.cost_c) === null ? <Dash /> : signedMoney(e.cost_c)),
    },
    {
      label: 'Collected',
      className: 'hidden sm:table-cell text-right tabular-nums',
      render: (e) => (num(e.collected_c) === null ? <Dash /> : signedMoney(e.collected_c)),
    },
    {
      label: 'Kept',
      className: 'text-right tabular-nums',
      render: (e) =>
        hasNumbers(e) ? (
          <span className={profitOf(e) >= 0 ? 'font-semibold text-success' : 'font-semibold text-destructive'}>
            {signedMoney(profitOf(e))}
          </span>
        ) : (
          <Dash />
        ),
    },
    {
      label: 'Actions',
      className: 'w-0 text-right',
      render: (e) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" aria-label={`Edit ${e.Name || 'entry'}`} onClick={() => edit(e)}>
            <ApperIcon name="Pencil" size={16} />
          </Button>
          <ConfirmDelete
            title="Delete this ledger entry?"
            description="It comes out of your totals. The order and its payments are not touched."
            onConfirm={() => remove(e)}
            variant="ghost"
          />
        </div>
      ),
    },
  ];

  const metricValue = (v) => (agg ? v : '…');

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Store"
        title="Ledger"
        description="Credits you used, what they cost you and what you collected. One entry opens by itself each time you activate or extend a line; add other costs by hand."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/admin/ledger/rates">
                <ApperIcon name="Coins" size={16} />
                Credit rates
              </Link>
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <ApperIcon name="Plus" size={16} />
              Add entry
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Period">
        {[...PERIODS, 'all'].map((p) => (
          <Pill key={p} on={period === p} onClick={() => setPeriod(p)}>
            {PERIOD_LABELS[p] ?? 'All time'}
          </Pill>
        ))}
      </div>

      {totals.error ? (
        <p className="mb-6 text-sm text-destructive">
          Totals did not load: {totals.error}{' '}
          <button type="button" className="underline" onClick={() => totals.run()}>
            Try again
          </button>
        </p>
      ) : (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Metric
            label="Credits used"
            value={metricValue(credits(agg ? agg('credits') : 0))}
            hint={`${periodLabel} · ${agg ? agg('entries') : 0} entries`}
            icon="Coins"
          />
          <Metric label="Cost to you" value={metricValue(signedMoney(cost))} hint={periodLabel} icon="ArrowDownRight" tone="text-destructive" />
          <Metric label="Collected" value={metricValue(signedMoney(collected))} hint={periodLabel} icon="ArrowUpRight" tone="text-success" />
          <Metric
            label="You kept"
            value={metricValue(signedMoney(kept))}
            hint={collected > 0 ? `${Math.round((kept / collected) * 100)}% of what you collected` : periodLabel}
            icon="PiggyBank"
            tone={kept >= 0 ? 'text-success' : 'text-destructive'}
          />
        </div>
      )}

      {needsCount > 0 && !needs && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning-border bg-warning-muted px-4 py-3 text-sm">
          <p className="flex items-center gap-2">
            <ApperIcon name="TriangleAlert" size={16} className="text-warning" />
            {needsCount === 1 ? '1 entry still needs' : `${needsCount} entries still need`} credits, cost or collected typed in.
          </p>
          <Button variant="outline" size="sm" onClick={() => setNeeds(true)}>
            Show them
          </Button>
        </div>
      )}

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search entries or order codes…" />
        <FilterSelect value={kind} onChange={setKind} values={LEDGER_KINDS} labels={LEDGER_KIND_LABELS} allLabel="All kinds" />
        <Pill on={needs} onClick={() => setNeeds((v) => !v)}>
          Needs numbers
        </Pill>
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'BookOpenCheck',
          title: filtered ? 'No entries match' : `No entries for ${periodLabel.toLowerCase()}`,
          description: filtered
            ? 'Try another search or filter.'
            : 'Activate or extend a line and an entry opens here. Add credit top-ups and other costs by hand.',
          action: filtered ? clear : () => setCreateOpen(true),
          actionLabel: filtered ? 'Clear filters' : 'Add entry',
        }}
      >
        <DataTable columns={columns} rows={list} />
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>

      <LedgerDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={refresh} />
      <LedgerDialog open={editOpen} onOpenChange={setEditOpen} entry={editing} onSaved={refresh} />
    </div>
  );
}
